import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { Button, Row, Col } from "react-bootstrap";
import "./business_form.css";
import { baseUrl } from "../../../../../network/constants/constants";
import config, { getConfig } from "../../../../../network/auth";
import axios from "../../../../../network/api";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const BusinessForm = ({
  show,
  handleClose,
  handleSubmit,
  business,
  categoryList = [], // Default to empty array
}) => {
  const [formData, setFormData] = useState({
    business_title: "",
    mobile_number: "",
    category_id: "",
    description: "",
    service_range_km: "",
    service_localities: "",
    business_place: "",
    latitude: "",
    longitude: "",
    logo: null,
    thumb: null,
    banner: null,
    phone: "",
    name: "",
    user_place: "",
  });

  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [userPlace, setUserPlace] = useState("");
  const [phoneOptions, setPhoneOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [errors, setErrors] = useState({
    phone: "",
    name: "",
    user_place: "",
    business_title: "",
    mobile_number: "",
  });

  const memoizedConfig = useMemo(() => {
    return getConfig();
  }, []);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isAutocompleteFailed, setIsAutocompleteFailed] = useState(false);
  const [phoneDirty, setPhoneDirty] = useState(false);

  // Initialize form data when business prop changes
  useEffect(() => {
    if (business) {
      // Edit mode - populate form with existing business data
      setFormData({
        business_title: business.business_title || "",
        mobile_number: business.mobile_number || "",
        category_id: business.category_id || "",
        description: business.description || "",
        service_range_km: business.service_range_km || "",
        service_localities: business.service_localities || "",
        business_place: business.place || "", // API uses 'place' field
        latitude: business.latitude || "",
        longitude: business.longitude || "",
        logo: null, // Don't pre-populate files
        thumb: null,
        banner: null,
        phone: business.phone || "",
        name: business.name || "",
        user_place: business.user_place || "",
      });

      setSelectedUser({
        phone: business.phone || "",
        name: business.name || "",
        user_place: business.user_place || "",
      });

      setPhone(business.phone || "");
      setName(business.name || "");
      setUserPlace(business.user_place || "");
    } else {
      // Create mode - reset form
      setFormData({
        business_title: "",
        mobile_number: "",
        category_id: "",
        description: "",
        service_range_km: "",
        service_localities: "",
        business_place: "",
        latitude: "",
        longitude: "",
        logo: null,
        thumb: null,
        banner: null,
        phone: "",
        name: "",
        user_place: "",
      });

      setSelectedUser(null);
      setPhone("");
      setName("");
      setUserPlace("");
    }

    // Reset errors when business changes
    setErrors({
      phone: "",
      name: "",
      user_place: "",
      business_title: "",
      mobile_number: "",
    });
  }, [business]);

  // Search for users when inputValue changes (only for create mode)
  useEffect(() => {
    if (!business && inputValue && inputValue.length >= 3) {
      axios
        .post(
          `${baseUrl}app/filter-users/`,
          { phone_start: inputValue },
          memoizedConfig
        )
        .then((response) => {
          const users = response.data.users.map((user) => ({
            phone: user.phone,
            name: user.name,
            user_place: user.place, // Note: API returns 'place', not 'user_place'
          }));
          setPhoneOptions(users);
        })
        .catch((error) => {
          console.error("Error fetching users:", error);
          setPhoneOptions([]);
        });
    } else {
      setPhoneOptions([]);
    }
  }, [inputValue, business, memoizedConfig]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear related errors
    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    console.log(`File selected for ${e.target.name}:`, file);

    setFormData({
      ...formData,
      [e.target.name]: file || null,
    });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, "").slice(0, 10);
    setPhone(numericValue);
    setPhoneDirty(true);

    setFormData((prev) => ({
      ...prev,
      mobile_number: numericValue,
    }));

    if (/^\d{10}$/.test(numericValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, phone: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // For create mode, validate phone
    if (!business) {
      if (!phone || !/^\d{10}$/.test(phone)) {
        newErrors.phone = "Please enter a valid 10-digit phone number.";
      }

      // If no existing user selected, name and place are required
      if (!selectedUser) {
        if (!name.trim()) {
          newErrors.name = "Name is required for new users.";
        }
        if (!userPlace.trim()) {
          newErrors.user_place = "User place is required for new users.";
        }
      }
    }

    // Business validations (for both create and edit)
    if (!formData.business_title.trim()) {
      newErrors.business_title = "Business name is required.";
    }
    if (!formData.mobile_number || !/^\d{10}$/.test(formData.mobile_number)) {
      newErrors.mobile_number = "Please enter a valid 10-digit mobile number.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Create FormData object
    const formDataToSend = new FormData();

    // Append business fields according to API schema
    formDataToSend.append("business_title", formData.business_title);
    formDataToSend.append("mobile_number", formData.mobile_number);
    formDataToSend.append("category_id", formData.category_id);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("service_range_km", formData.service_range_km);
    formDataToSend.append(
      "service_localities",
      formData.service_localities || ""
    );
    formDataToSend.append("place", formData.business_place); // API expects 'place', not 'business_place'
    formDataToSend.append("latitude", formData.latitude);
    formDataToSend.append("longitude", formData.longitude);

    // Add business status fields if in edit mode
    if (business) {
      formDataToSend.append(
        "business_is_listed",
        business.business_is_listed || true
      );
      formDataToSend.append(
        "business_is_approved",
        business.business_is_approved || false
      );
      formDataToSend.append("owner", business.owner || business.id);
    }

    // Append files only if they exist and are File objects
    if (
      formData.logo &&
      formData.logo instanceof File &&
      formData.logo.size > 0
    ) {
      formDataToSend.append("logo", formData.logo);
    }
    if (
      formData.thumb &&
      formData.thumb instanceof File &&
      formData.thumb.size > 0
    ) {
      formDataToSend.append("thumb", formData.thumb);
    }
    if (
      formData.banner &&
      formData.banner instanceof File &&
      formData.banner.size > 0
    ) {
      formDataToSend.append("banner", formData.banner);
    }

    // For create mode, append user fields
    if (!business) {
      formDataToSend.append("phone", phone);
      formDataToSend.append("name", name);
      formDataToSend.append("user_place", userPlace);
    }

    // Debug: Log FormData contents
    console.log("FormData being sent:");
    for (let [key, value] of formDataToSend.entries()) {
      console.log(key, value);
    }

    handleSubmit(formDataToSend);
  };

  return (
    <Modal
      size="lg"
      fullscreen="sm-down"
      className="business-model"
      show={show}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {business ? "Edit Business" : "Add Business and User"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="add-category-div">
        <Form onSubmit={handleFormSubmit}>
          {!business && (
            <>
              <Modal.Header className="user-heading">User Details</Modal.Header>
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="formPhoneNumber">
                    <Form.Label>Phone Number</Form.Label>
                    {isAutocompleteFailed ? (
                      <Form.Control
                        type="tel"
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder="Enter phone number"
                        className={errors.phone ? "is-invalid" : ""}
                      />
                    ) : (
                      <Autocomplete
                        freeSolo
                        disablePortal
                        id="phone-autocomplete"
                        options={phoneOptions}
                        getOptionLabel={(option) =>
                          `${option.phone} - ${option.name}`
                        }
                        inputValue={inputValue}
                        onInputChange={(event, newInputValue) => {
                          const numericValue = newInputValue.replace(/\D/g, "");
                          setInputValue(numericValue);
                          setPhone(numericValue);
                          setPhoneDirty(true);
                          setFormData((prev) => ({ ...prev, mobile_number: numericValue }));

                          if (errors.phone) {
                            setErrors((prev) => ({ ...prev, phone: "" }));
                          }
                        }}
                        onChange={(event, newValue) => {
                          if (newValue && typeof newValue === "object") {
                            // Existing user selected
                            setSelectedUser(newValue);
                            setPhone(newValue.phone);
                            setInputValue(newValue.phone);
                            setName(newValue.name);
                            setUserPlace(newValue.user_place);
                            setFormData((prev) => ({ ...prev, mobile_number: newValue.phone }));
                            setIsAutocompleteFailed(false);
                            setErrors((prevErrors) => ({
                              ...prevErrors,
                              phone: "",
                            }));
                          } else {
                            // New user or cleared selection
                            setSelectedUser(null);
                            const phoneValue = newValue || inputValue;
                            setPhone(phoneValue);
                            setInputValue(phoneValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            type="tel"
                            label="Phone Number"
                            size="small"
                            placeholder="Enter 10-digit phone number"
                            error={!!errors.phone}
                            helperText={
                              errors.phone ||
                              (phone && phone.length === 10 && !selectedUser
                                ? "New user will be created"
                                : "")
                            }
                            inputProps={{
                              ...params.inputProps,
                              pattern: "\\d*",
                              inputMode: "numeric",
                              maxLength: 10,
                            }}
                          />
                        )}
                      />
                    )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="formUserName">
                    <Form.Label>
                      User Name
                      {!selectedUser && <span className="text-danger"> *</span>}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (errors.name) {
                          setErrors((prev) => ({ ...prev, name: "" }));
                        }
                      }}
                      placeholder={
                        selectedUser
                          ? "Auto-filled from existing user"
                          : "Enter user name"
                      }
                      disabled={!!selectedUser}
                      className={errors.name ? "is-invalid" : ""}
                    />
                    {errors.name && (
                      <Form.Text className="text-danger">
                        {errors.name}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="formPlace">
                    <Form.Label>
                      User Place
                      {!selectedUser && <span className="text-danger"> *</span>}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={userPlace}
                      onChange={(e) => {
                        setUserPlace(e.target.value);
                        if (errors.user_place) {
                          setErrors((prev) => ({ ...prev, user_place: "" }));
                        }
                      }}
                      placeholder={
                        selectedUser
                          ? "Auto-filled from existing user"
                          : "Enter user place"
                      }
                      disabled={!!selectedUser}
                      className={errors.user_place ? "is-invalid" : ""}
                    />
                    {errors.user_place && (
                      <Form.Text className="text-danger">
                        {errors.user_place}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          <Modal.Header className="business-heading">
            Business Details
          </Modal.Header>

          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Group controlId="formCategoryName">
                <Form.Label>
                  Business Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="business_title"
                  value={formData.business_title}
                  onChange={handleChange}
                  required
                  className={errors.business_title ? "is-invalid" : ""}
                />
                {errors.business_title && (
                  <Form.Text className="text-danger">
                    {errors.business_title}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="formMobileNumber">
                <Form.Label>
                  Contact Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  placeholder="Enter 10-digit mobile number"
                  required
                  className={errors.mobile_number ? "is-invalid" : ""}
                />
                {errors.mobile_number && (
                  <Form.Text className="text-danger">
                    {errors.mobile_number}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Group controlId="formCategorySelect">
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categoryList && categoryList.length > 0 ? (
                    categoryList.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading categories...</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="formPlaceBusiness">
                <Form.Label>
                  Business Place <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="business_place"
                  value={formData.business_place}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Group controlId="formLatitude">
                <Form.Label>
                  Latitude <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="formLongitude">
                <Form.Label>
                  Longitude <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <Form.Group controlId="formDescription">
                <Form.Label>
                  Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <Form.Group controlId="formServiceRangeKm">
                <Form.Label>
                  Service Range (KM) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="service_range_km"
                  value={formData.service_range_km}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <Form.Group controlId="formServiceLocalities">
                <Form.Label>Service Localities</Form.Label>
                <Form.Control
                  type="text"
                  name="service_localities"
                  value={formData.service_localities}
                  onChange={handleChange}
                  placeholder="Place,Sub place"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={4}>
              <Form.Group controlId="formLogo">
                <Form.Label>Logo</Form.Label>
                <Form.Control
                  type="file"
                  name="logo"
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {business && business.logo && (
                  <small className="text-muted">
                    Current: {business.logo.split("/").pop()}
                  </small>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group controlId="formThumb">
                <Form.Label>Thumb</Form.Label>
                <Form.Control
                  type="file"
                  name="thumb"
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {business && business.thumb && (
                  <small className="text-muted">
                    Current: {business.thumb.split("/").pop()}
                  </small>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group controlId="formBanner">
                <Form.Label>Banner</Form.Label>
                <Form.Control
                  type="file"
                  name="banner"
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {business && business.banner && (
                  <small className="text-muted">
                    Current: {business.banner.split("/").pop()}
                  </small>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {business ? "Update Business" : "Add Business"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default BusinessForm;
